import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { FormModal, TextField, TextArea } from "@/components/FormKit";
import { Loader2, ShieldCheck, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ProfilesPage() {
  const [profiles, setProfiles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", description: "", permissions: [] });
  const [submitting, setSubmitting] = useState(false);

  const load = () => { setLoading(true); api.get("/profiles").then((r) => setProfiles(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/permissions/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const groups = catalog.reduce((acc, p) => { (acc[p.group] = acc[p.group] || []).push(p); return acc; }, {});

  const openEdit = (p) => {
    setEditing(p);
    setForm({ code: p.code, name: p.name, description: p.description || "", permissions: p.permissions.includes("*") ? catalog.map((c) => c.code) : [...p.permissions] });
    setOpen(true);
  };
  const openNew = () => { setEditing(null); setForm({ code: "", name: "", description: "", permissions: [] }); setOpen(true); };

  const toggle = (code) => setForm((f) => ({ ...f, permissions: f.permissions.includes(code) ? f.permissions.filter((c) => c !== code) : [...f.permissions, code] }));

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editing) {
        const isAdmin = editing.permissions.includes("*");
        await api.put(`/profiles/${editing.id}`, { name: form.name, description: form.description, permissions: isAdmin ? ["*"] : form.permissions });
      } else {
        await api.post("/profiles", { code: form.code.toUpperCase(), name: form.name, description: form.description, permissions: form.permissions });
      }
      toast.success("Perfil salvo."); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSubmitting(false); }
  };

  const isAdminProfile = editing?.permissions?.includes("*");

  return (
    <div className="space-y-6">
      <PageHeader title="Perfis e Permissões" subtitle="Controle de acesso flexível por perfil"
        actions={<button onClick={openNew} data-testid="add-profile-button" className="bg-brand hover:bg-brand-hover text-white font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Novo Perfil</button>} />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
        : profiles.length === 0 ? <EmptyState />
        : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {profiles.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid={`profile-card-${p.code}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-brand-light text-brand flex items-center justify-center"><ShieldCheck className="h-5 w-5" /></div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900">{p.name}</h3>
                    <div className="text-xs text-slate-400 font-mono">{p.code}</div>
                  </div>
                </div>
                <button onClick={() => openEdit(p)} data-testid={`edit-profile-${p.code}`} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>
              </div>
              <p className="text-sm text-slate-500 mt-3">{p.description}</p>
              <div className="mt-3 text-xs text-slate-400">
                {p.permissions.includes("*") ? <span className="font-semibold text-gold">Acesso total ao sistema</span> : `${p.permissions.length} permissões atribuídas`}
              </div>
            </div>
          ))}
        </div>
      )}

      <FormModal open={open} onOpenChange={setOpen} title={editing ? `Editar Perfil — ${editing.name}` : "Novo Perfil"} onSubmit={submit} submitting={submitting} testId="profile-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {!editing && <TextField label="Código" value={form.code} onChange={(v) => setForm((f) => ({ ...f, code: v }))} testId="profile-code" required />}
          <TextField label="Nome" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testId="profile-name" required className={editing ? "sm:col-span-2" : ""} />
          <TextArea label="Descrição" value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} testId="profile-description" className="sm:col-span-2" />
        </div>
        {isAdminProfile ? (
          <div className="bg-gold-light border border-gold-border rounded-lg px-4 py-3 text-sm text-slate-700">Este é o perfil de administrador com acesso total. As permissões não podem ser restringidas.</div>
        ) : (
          <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-1">
            {Object.entries(groups).map(([g, perms]) => (
              <div key={g}>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{g}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {perms.map((perm) => (
                    <label key={perm.code} className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-100">
                      <input type="checkbox" checked={form.permissions.includes(perm.code)} onChange={() => toggle(perm.code)} data-testid={`perm-${perm.code}`} className="rounded border-slate-300 text-brand focus:ring-brand" />
                      {perm.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </FormModal>
    </div>
  );
}
